import {useCallback, useEffect, useMemo, useState} from 'react';
import {useParams, useHistory} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {useQuery, QueryFunctionContext} from 'react-query';

import {
  DataItem,
  ListResponse,
  PaginatedResponse,
  Upload,
  User,
  Vehicle,
} from 'types';

import api from 'api';
import roles from 'constants/roles';
import states from 'constants/states';
import {get} from 'lodash';
import useDeleteEmployee from './useDeleteEmployee';
import toast from 'react-hot-toast';
import {phoneFormat} from 'helpers/phoneFormat';
import useAuth from 'hooks/useAuth';
import useSort from 'hooks/useSort';
import {format} from 'date-fns';
interface Option {
  label: string;
  value: string | number;
}

interface FormValue {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  organisation: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zipcode: string;
  state: string;
  npi_no: string;
  username: string;
  password: string;
  password_confirmation: string;
  latitude: number;
  longitude: number;
  role: Option;
  vehicle?: Option;
  is_active: boolean;
  license_front: Upload;
  license_back: Upload;
  document_number?: string;
  document_state?: Option;
  issue_state?: Option;
}

async function getPrescriptions({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, page, pageSize] = queryKey;

  if (!userId) return undefined;

  const {data} = await api.get<PaginatedResponse<any>>(`/requests/`, {
    params: {
      offset: +page * Number(pageSize),
      limit: Number(pageSize),
      prescriber_user_id: userId,
      source: 'micromerchant',
    },
  });

  return data;
}

async function getCustomers({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId, page, pageSize, order, metaDatas] = queryKey;
  const {data} = await api.get<ListResponse<any>>(`/customers/`, {
    params: {
      order: order,
      limit: Number(pageSize),
      offset: +page * Number(pageSize),
      prescriber_user_id: userId,
      last_prescription_date_after: metaDatas.last_prescription_date.startDate
        ? format(metaDatas.last_prescription_date.startDate, 'yyyy-MM-dd')
        : undefined,
      last_prescription_date_before: metaDatas.last_prescription_date.endDate
        ? format(metaDatas.last_prescription_date.endDate, 'yyyy-MM-dd')
        : undefined,
      total_prescriptions_range_min: metaDatas.total_prescriptions_range.from
        ? metaDatas.total_prescriptions_range.from
        : undefined,
      total_prescriptions_range_max: metaDatas.total_prescriptions_range.to
        ? metaDatas.total_prescriptions_range.to
        : undefined,
      date_joined_after: metaDatas.dataJoined.startDate
        ? format(metaDatas.dataJoined.startDate, 'yyyy-MM-dd')
        : undefined,
      date_joined_before: metaDatas.dataJoined.endDate
        ? format(metaDatas.dataJoined.endDate, 'yyyy-MM-dd')
        : undefined,
    },
  });

  return data;
}

async function getPermissions({queryKey}: QueryFunctionContext<any[]>) {
  const [,] = queryKey;
  const {data} = await api.get<any[]>(`/permissions/menus/`, {
    params: {
      limit: 30,
    },
  });

  return data;
}

async function getUserPermissions({queryKey}: QueryFunctionContext<any[]>) {
  const [, userId] = queryKey;
  const {data} = await api.get<any[]>(
    `/permissions/user-permissions/${userId}/`
  );

  return data;
}

async function getVehicles() {
  const {data} = await api.get<ListResponse<Vehicle>>('/vehicles/');

  return data;
}

async function getEmployeeById({queryKey}: QueryFunctionContext) {
  const [, employeeId] = queryKey;

  if (!employeeId) return undefined;

  const {data} = await api.get<User>(`/users/${employeeId}`);

  return data;
}

function useEmployeeForm() {
  const {currentUser, rolePermission} = useAuth();
  const {employeeId} = useParams<{employeeId: string}>();
  const {push, replace, location} = useHistory();

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(
    location.pathname.startsWith('/prescribers')
      ? sessionStorage.getItem('employee_tab') || ''
      : ''
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deleteModal, setDeleteModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [viaSms, setViaSms] = useState(false);
  const {deleteEmployee, isDeleting} = useDeleteEmployee();

  const [userPermissions, setUserPermissions] = useState<Record<string, any>>(
    {}
  );
  const [filterVisible, setFilterVisible] = useState(false);
  const [filter, setFilter] = useState({
    last_prescription_date: {
      startDate: sessionStorage.getItem(
        'last_prescription_date_start_prescriber'
      )
        ? new Date(
            JSON.parse(
              sessionStorage.getItem(
                'last_prescription_date_start_prescriber'
              ) || ''
            )
          )
        : null,
      endDate: sessionStorage.getItem('last_prescription_date_end_prescriber')
        ? new Date(
            JSON.parse(
              sessionStorage.getItem('last_prescription_date_end_prescriber') ||
                ''
            )
          )
        : null,
    },
    dataJoined: {
      startDate: sessionStorage.getItem('data_joined_start_prescriber')
        ? new Date(
            JSON.parse(
              sessionStorage.getItem('data_joined_start_prescriber') || ''
            )
          )
        : null,
      endDate: sessionStorage.getItem('data_joined_end_prescriber')
        ? new Date(
            JSON.parse(
              sessionStorage.getItem('data_joined_end_prescriber') || ''
            )
          )
        : null,
    },
    total_prescriptions_range: {
      from: sessionStorage.getItem('total_prescriptions_range_from_prescriber')
        ? JSON.parse(
            sessionStorage.getItem(
              'total_prescriptions_range_from_prescriber'
            ) || ''
          )
        : null,
      to: sessionStorage.getItem('total_prescriptions_range_to_prescriber')
        ? JSON.parse(
            sessionStorage.getItem('total_prescriptions_range_to_prescriber') ||
              ''
          )
        : null,
    },
  });
  const [metaDatas, setMetaDatas] = useState<any>({
    last_prescription_date: {
      startDate: sessionStorage.getItem(
        'last_prescription_date_start_prescriber'
      )
        ? new Date(
            JSON.parse(
              sessionStorage.getItem(
                'last_prescription_date_start_prescriber'
              ) || ''
            )
          )
        : null,
      endDate: sessionStorage.getItem('last_prescription_date_end_prescriber')
        ? new Date(
            JSON.parse(
              sessionStorage.getItem('last_prescription_date_end_prescriber') ||
                ''
            )
          )
        : null,
    },
    dataJoined: {
      startDate: sessionStorage.getItem('data_joined_start_prescriber')
        ? new Date(
            JSON.parse(
              sessionStorage.getItem('data_joined_start_prescriber') || ''
            )
          )
        : null,
      endDate: sessionStorage.getItem('data_joined_end_prescriber')
        ? new Date(
            JSON.parse(
              sessionStorage.getItem('data_joined_end_prescriber') || ''
            )
          )
        : null,
    },
    total_prescriptions_range: {
      from: sessionStorage.getItem('total_prescriptions_range_from_prescriber')
        ? JSON.parse(
            sessionStorage.getItem(
              'total_prescriptions_range_from_prescriber'
            ) || ''
          )
        : null,
      to: sessionStorage.getItem('total_prescriptions_range_to_prescriber')
        ? JSON.parse(
            sessionStorage.getItem('total_prescriptions_range_to_prescriber') ||
              ''
          )
        : null,
    },
  });

  const {
    handleSubmit,
    register,
    control,
    formState: {errors},
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<FormValue>();

  const role = watch('role');
  const license_front = watch('license_front');
  const license_back = watch('license_back');

  const {sortBy, sortOrder, order, onSort} = useSort('id', 'desc');

  const license_files = useMemo(
    function () {
      return [license_front, license_back].filter((item) => Boolean(item));
    },
    [license_front, license_back]
  );

  const {data: prescriptions, isLoading: prescriptionsLoading} = useQuery(
    ['user_prescriptions', employeeId, currentPage - 1, pageSize],
    getPrescriptions,
    {enabled: role?.label === 'Prescriber'}
  );
  const {data: customers, isLoading: customersLoading} = useQuery(
    ['user_customers', employeeId, currentPage - 1, pageSize, order, metaDatas],
    getCustomers
  );

  const {data: permissions} = useQuery(['permissions_menu'], getPermissions, {
    enabled: currentUser?.role === 'management',
  });

  const {data: user_permissions} = useQuery(
    ['permissions_user', employeeId],
    getUserPermissions,
    {
      enabled: currentUser?.role === 'management',
      refetchOnWindowFocus: false,
    }
  );

  const {data: vehicles} = useQuery('vehicles', getVehicles);
  const {data: employee} = useQuery(['employee', employeeId], getEmployeeById);

  const options = useMemo(
    function () {
      if (!vehicles) return [];

      return vehicles.results
        .filter((vehicle) => !vehicle.is_assigned)
        .map((v) => ({value: v.id, label: v.nickname}));
    },
    [vehicles]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [status]);

  useEffect(() => {
    if (user_permissions) {
      const permissions: Record<string, any> = {};
      user_permissions.forEach((item) => {
        permissions[item.menu.id] = {
          ...item,
        };
      });
      setUserPermissions(permissions);
    }
  }, [user_permissions]);

  useEffect(
    function () {
      if (employee) {
        const {driver, ...user} = employee;
        if (!user) {
          return;
        }

        reset({
          ...user,
          phone: phoneFormat(user.phone),
          address_line_1: get(user, 'address.address_line_1', ''),
          organisation: get(user, 'organisation', ''),
          address_line_2: get(user, 'address.address_line_2', ''),
          city: get(user, 'address.city', ''),
          zipcode: get(user, 'address.zipcode', ''),
          state: get(user, 'address.state', ''),
          role: roles.find((r) => r.value === employee.role),
          vehicle: employee.driver?.vehicle
            ? {
                value: employee.driver?.vehicle.id,
                label: employee.driver?.vehicle.nickname,
              }
            : {value: 0, label: ''},
          document_number: driver?.document_number,
          document_state: {value: driver?.document_state},
          issue_state: states.find((s) => s.value === driver?.issue_state),
          license_front: driver?.license_front,
          license_back: driver?.license_back,
        });
      }
    },
    [employee, reset]
  );

  const handleFileUpload = useCallback(
    function (uploads: Upload[]) {
      setValue('license_front', uploads[0]);
      setValue('license_back', uploads[1]);
    },
    [setValue]
  );

  async function submit(data: FormValue) {
    setSubmitting(true);

    const user =
      role.value === 'prescriber'
        ? {
            ...data,
            address: {
              address_line_1: data.address_line_1,
              address_line_2: data.address_line_2,
              city: data.city,
              zipcode: data.zipcode,
              state: data.state,
              latitude: data.latitude,
              longitude: data.longitude,
            },
            first_name: data.first_name,
            last_name: data.last_name,
            username: data.username,
            email: data.email,
            phone: data.phone.replace(/\D/g, ''),
            npi_no: data.npi_no,
            role: data.role.value,
            is_active: data.is_active,
            password: data.password,
          }
        : {
            ...data,
            phone: data.phone.replace(/\D/g, ''),
            role: data.role.value,
          };

    try {
      const {data: userData} = await (employeeId
        ? api.patch<User>(`/users/${employeeId}/`, user)
        : api.post<User>('/users/', user));

      if (currentUser?.role === 'management' && employeeId) {
        const menu_permissions = Object.values(userPermissions).map((menu) => {
          return {
            menu_slug: menu.menu.slug,
            permissions: menu.permissions,
          };
        });
        await api.post('/permissions/assign-permission/', {
          user_id: employeeId,
          menu_permissions,
        });
      }

      if (user.role === 'driver') {
        const driver = {
          id: employee?.driver?.id,
          document_number: data.document_number,
          document_state: data.document_state?.value,
          issue_state: data.issue_state?.value,
          vehicle: data.vehicle?.value,
          license_front: data.license_front?.id,
          license_back: data.license_back?.id,
          user: userData.id,
        };

        if (driver.id) {
          await api.patch(`/drivers/${driver.id}/`, driver);
        } else {
          await api.post('/drivers/', driver);
        }
      }
      setSubmitting(false);

      push(
        role.value === 'prescriber' ? '/prescribers' : '/settings/employees'
      );
    } catch (error) {
      const errors = get(error, 'response,data', {});
      const error_arr = Object.values(errors);
      toast.error(
        (get(error_arr, '[0]', '') as string) || 'Something went wrong'
      );
      setSubmitting(false);
    }
  }

  const handleDeleteEmployee = useCallback(
    async (id: number) => {
      await deleteEmployee(id);

      replace(
        role.label === 'Prescriber' ? '/prescribers' : '/settings/employees'
      );
    },
    [deleteEmployee, role, replace]
  );

  const handleNavigateRequest = useCallback(
    (row: DataItem) => {
      push(`/orders/${row.id}`);
    },
    [push]
  );

  const handleInvitePrescriber = useCallback(
    async (isSms: boolean) => {
      try {
        await api.post(
          `/prescribers/${get(
            employee,
            'prescriber.id',
            0
          )}/send-notification/`,
          {
            is_sms: isSms,
            is_email: !isSms,
          }
        );
        setConfirmModal(false);
        toast.success('Prescriber invited successfully');
      } catch (error) {
        toast.error('Prescriber invitation failed');
      }
    },
    [employee]
  );

  const onChangePermission = (
    menu: number,
    permission: 'write' | 'read' | 'delete'
  ) => {
    const copied = {...userPermissions};
    copied[menu].permissions[permission] =
      !copied[menu].permissions[permission];
    setUserPermissions(copied);
  };

  const resetFilter = () => {
    setFilter({
      last_prescription_date: {
        startDate: null,
        endDate: null,
      },
      total_prescriptions_range: {
        from: null,
        to: null,
      },
      dataJoined: {
        startDate: null,
        endDate: null,
      },
    });
    setMetaDatas({
      last_prescription_date: {
        startDate: null,
        endDate: null,
      },
      total_prescriptions_range: {
        from: null,
        to: null,
      },
      dataJoined: {
        startDate: null,
        endDate: null,
      },
    });
    sessionStorage.removeItem('last_prescription_date_start_prescriber');
    sessionStorage.removeItem('last_prescription_date_end_prescriber');
    sessionStorage.removeItem('total_prescriptions_range_from_prescriber');
    sessionStorage.removeItem('total_prescriptions_range_to_prescriber');
    sessionStorage.removeItem('data_joined_start_prescriber');
    sessionStorage.removeItem('data_joined_end_prescriber');
    setFilterVisible(false);
  };

  const applyFilter = () => {
    if (filter.last_prescription_date.startDate) {
      sessionStorage.setItem(
        'last_prescription_date_start_prescriber',
        JSON.stringify(filter.last_prescription_date.startDate || '')
      );
    } else {
      sessionStorage.removeItem('last_prescription_date_start_prescriber');
    }
    if (filter.last_prescription_date.endDate) {
      sessionStorage.setItem(
        'last_prescription_date_end_prescriber',
        JSON.stringify(filter.last_prescription_date.endDate || '')
      );
    } else {
      sessionStorage.removeItem('last_prescription_date_end_prescriber');
    }
    if (filter.dataJoined.startDate) {
      sessionStorage.setItem(
        'data_joined_start_prescriber',
        JSON.stringify(filter.dataJoined.startDate || '')
      );
    } else {
      sessionStorage.removeItem('data_joined_start_prescriber');
    }
    if (filter.dataJoined.endDate) {
      sessionStorage.setItem(
        'data_joined_end_prescriber',
        JSON.stringify(filter.dataJoined.endDate || '')
      );
    } else {
      sessionStorage.removeItem('data_joined_end_prescriber');
    }
    if (filter.total_prescriptions_range.from) {
      sessionStorage.setItem(
        'total_prescriptions_range_from_prescriber',
        JSON.stringify(filter.total_prescriptions_range.from || '')
      );
    } else {
      sessionStorage.removeItem('total_prescriptions_range_from_prescriber');
    }
    if (filter.total_prescriptions_range.to) {
      sessionStorage.setItem(
        'total_prescriptions_range_to_prescriber',
        JSON.stringify(filter.total_prescriptions_range.to || '')
      );
    } else {
      sessionStorage.removeItem('total_prescriptions_range_to_prescriber');
    }
    setMetaDatas(filter);
    setCurrentPage(1);
    setFilterVisible(false);
  };

  const canDelete =
    role?.value === 'prescriber'
      ? rolePermission['employees']?.permissions?.delete
      : rolePermission['employees']?.permissions?.delete;
  const canUpdate =
    role?.value === 'prescriber'
      ? rolePermission['employees']?.permissions?.write
      : rolePermission['employees']?.permissions?.write;

  const filtered = useMemo(() => {
    return Object.values(filter).some((obj) =>
      Object.values(obj).some((value) => !!value)
    );
  }, [filter]);

  return {
    currentUser,
    employeeId,
    options,
    submitting,
    errors,
    control,
    role: role?.label,
    license_files,
    register,
    handleFileUpload,
    getValues,
    setValue,
    watch,
    status,
    setStatus,
    userPermissions,
    prescriptions: prescriptions?.results || [],
    prescriptionsLoading,
    customers: customers?.results || [],
    customersLoading,
    totalPages:
      status === 'patients'
        ? Math.ceil((customers?.count || 0) / pageSize)
        : Math.ceil((prescriptions?.count || 0) / pageSize),
    count: status === 'patients' ? customers?.count : prescriptions?.count,
    currentPage,
    pageSize,
    setPageSize,
    setCurrentPage,
    employee,
    deleteModal,
    setDeleteModal,
    isDeleting,
    handleDeleteEmployee,
    handleNavigateRequest,
    handleInvitePrescriber,
    handleSubmit: handleSubmit(submit),
    confirmModal,
    setConfirmModal,
    viaSms,
    setViaSms,
    canDelete,
    canUpdate,
    permissions: permissions || [],
    onChangePermission,
    sortBy,
    sortOrder,
    onSort,
    order,
    filtered,
    setFilterVisible,
    filter,
    setFilter,
    applyFilter,
    resetFilter,
    filterVisible,
  };
}

export default useEmployeeForm;
